import React from 'react';
import { NextPage } from 'next';
import { useRouter } from 'next/router';
import { reduxWrapper } from 'store';
import { match, P } from 'ts-pattern';
import { getProviderKinds } from 'lib/DocumentsClients/providers';
import type { GetProviderKindsResponse } from 'types/api/documents/providers';
import { OkResult, ResultPropType, toResultProp } from 'utils/results';
import { captureResult } from 'utils/sentry';
import ProvidersFilterPageView from 'views/ProvidersPage/filter';
import { REVALIDATE_TIME } from 'constants/Revalidate';
import { initializeApplication } from 'state/actions/applicationActions';
import { ApplicationContext } from 'types';

interface Props {
  result: OkResult<GetProviderKindsResponse>;
}

const ProviderKindsListPage: NextPage<Props> = ({ result }: Props) => {
  const router = useRouter();
  const param = (s: string | string[] | undefined): string | null =>
    match(s)
      .with(P.string, () => `${s}`)
      .otherwise(() => null);

  return (
    <ProvidersFilterPageView
      routeParams={{
        kind: param(router.query.kind),
        state: param(router.query.state),
        city: param(router.query.city),
      }}
      items={result.value.items}
    />
  );
};

export const getStaticProps = reduxWrapper.getStaticProps(async ({ store }) => {
  const result = await getProviderKinds();
  captureResult(result);

  const formattedResult = toResultProp(result);

  if (formattedResult.type == ResultPropType.ERR) {
    return {
      notFound: true,
      revalidate: REVALIDATE_TIME,
    };
  }

  // _app.tsx#getInitialProps doesn't fire when building static pages. We have to initialize
  // the app here. TODO: think of a DRY'er way to do this, so we don't forget to when we build
  // more pages statically.
  await store.dispatch(
    initializeApplication({
      query: {},
      req: {},
    } as ApplicationContext) as any,
  );

  return {
    props: {
      result: formattedResult,
    },
    revalidate: REVALIDATE_TIME,
  };
});

export default ProviderKindsListPage;
